// Phase A.2.b BLOCKER FIX #2 · explicit Change Role dialog.
//
// Replaces the always-active row-inline `<select>` that made role
// changes accidental. Operator opens the dialog, picks a canonical
// role, clicks Save Role. Historic non-canonical values display but
// require re-selection to save.

import React, { useState } from "react";
import { X } from "lucide-react";
import { CANONICAL_ROLES, roleLabel } from "@/lib/relationshipRoles";

export function RoleChangeDialog({
  open,
  currentRole,
  title = "Change Role",
  personName = "",
  onClose,
  onSave,
  testidPrefix = "role-change",
}) {
  const isCanonical = CANONICAL_ROLES.some(([k]) => k === currentRole);
  const [role, setRole] = useState(isCanonical ? currentRole : "");
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose?.()}
      data-testid={`${testidPrefix}-dialog`}
    >
      <div
        className="bg-white w-full sm:max-w-sm rounded-t-md sm:rounded-md shadow-xl border border-coach-silver"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-coach-silver">
              {personName || "Relationship"}
            </div>
            <div className="text-[15px] font-medium mt-0.5">{title}</div>
          </div>
          <button
            onClick={onClose}
            className="text-coach-silver hover:text-white text-[12px] focus-visible:outline-2 focus-visible:outline-touring-lime px-2 py-1 rounded-sm"
            aria-label="Close"
            data-testid={`${testidPrefix}-close`}
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[11.5px] text-steel">
            Current role:{" "}
            <span className="text-carbon font-medium">
              {roleLabel(currentRole) || "—"}
            </span>
            {!isCanonical && currentRole && (
              <span className="ml-2 text-amber-hold">
                (legacy — pick a canonical role to save)
              </span>
            )}
          </div>
          <label className="block">
            <div className="text-[10.5px] uppercase tracking-widest text-steel mb-1">
              New role <span className="text-critical-red">*</span>
            </div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid={`${testidPrefix}-select`}
              aria-label="New role"
              className="w-full border border-coach-silver rounded-sm px-2 py-2 text-[13px] bg-white focus-visible:outline-2 focus-visible:outline-coach-blue"
            >
              <option value="">— Choose a role —</option>
              {CANONICAL_ROLES.map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="border-t border-coach-silver bg-road-white px-4 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid={`${testidPrefix}-cancel`}
            className="border border-coach-silver text-steel text-[12.5px] px-3 py-1.5 rounded-sm hover:bg-white focus-visible:outline-2 focus-visible:outline-coach-blue"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              if (!role || role === currentRole || saving) return;
              setSaving(true);
              try {
                await onSave(role);
              } finally {
                setSaving(false);
              }
            }}
            disabled={!role || role === currentRole || saving}
            data-testid={`${testidPrefix}-save`}
            className="bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-4 py-1.5 rounded-sm disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-touring-lime"
          >
            {saving ? "Saving…" : "Save Role"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RoleChangeDialog;
